import React, { useEffect, useState } from "react";
import { useDrop } from "react-dnd";
import { useAuth } from "../../../context/AuthContext";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
} from "@mui/material";

import { useSnackbar } from "notistack";

import "./Style.css";

import EditQuestionPopup from "./Fetures/Ouestions/EditQuestionPopup";
import BotPreviewDialogPopup from "./Fetures/Ouestions/BotPreviewDialogPopup";
import QuestionDraggableItem from "./Fetures/Ouestions/QuestionDraggableItem";
import { fetchUserById, updateUserDetails } from "../../../api/authApi";
import { getBotById, updateBot, listTemplates, applyTemplate, generateQuestions } from "../../../api/botApi";
import { toast, ToastContainer } from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import PreviewIcon from "@mui/icons-material/Preview";
import SaveIcon from "@mui/icons-material/Save";
import WidgetsIcon from "@mui/icons-material/Widgets";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import EmailIcon from "@mui/icons-material/Email";
import DialpadIcon from "@mui/icons-material/Dialpad";
import TagIcon from "@mui/icons-material/Tag";
import GradeIcon from "@mui/icons-material/Grade";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

// Display metadata for the question types the AI generator can return, so the
// generated items render in the canvas just like dragged-in components.
const AI_TYPE_META = {
  question: { label: "Question", color: "#6366f1", icon: <QuestionMarkIcon /> },
  single_choice: { label: "Single Choice", color: "#f59e0b", icon: <DoneOutlineIcon /> },
  multiple_options: { label: "Multiple Choice", color: "#8b5cf6", icon: <DoneAllIcon /> },
  email_feild: { label: "Email", color: "#10b981", icon: <EmailIcon /> },
  mobile_number: { label: "Mobile Number", color: "#ec4899", icon: <DialpadIcon /> },
  number: { label: "Number", color: "#06b6d4", icon: <TagIcon /> },
  rating: { label: "Rating", color: "#f97316", icon: <GradeIcon /> },
  date_picker: { label: "Date Picker", color: "#14b8a6", icon: <CalendarMonthIcon /> },
};

const FlowCanvasComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { questionTypes } = useAuth();

  const [droppedItems, setDroppedItems] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [language, setLanguage] = useState("en");
  const [openPreview, setOpenPreview] = useState(false);
  const [initialTab, setInitialTab] = useState(0);
  const [botSettings, setBotSettings] = useState({});
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiCount, setAiCount] = useState(5);
  const [aiLoading, setAiLoading] = useState(false);

  const [{ isOver }, dropRef] = useDrop(() => ({
    accept: "COMPONENT",
    drop: (item) => {
      if (item.paid && !questionTypes.includes(item.type)) {
        enqueueSnackbar("Upgrade your plan to use this component", {
          variant: "warning",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        return;
      }
      const defaultText = item.defaultLabel || "Default Question";
      setDroppedItems((prev) => [
        ...prev,
        { ...item, id: Date.now(), text: defaultText },
      ]);
      enqueueSnackbar("Question added successfully", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  }));

  const handleEdit = (item) => {
    setInitialTab(0);
    setEditingItem(item);
    setOpenEdit(true);
  };

  const generateUniqueId = () => "q" + Math.random().toString(36).substr(2, 9);

  const handleUpdate = (updatedItem) => {
    setDroppedItems((prev) =>
      prev.map((q) => (q.id === updatedItem.id ? updatedItem : q))
    );
  };

  const handleDelete = (id) => {
    setDroppedItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditingItem(null);
    setInitialTab(0);
  };

  const handleDuplicate = (item) => {
    const newItem = {
      ...item,
      id: generateUniqueId(),
      text: item.text + " (copy)",
    };
    setDroppedItems((prev) => [...prev, newItem]);
    enqueueSnackbar("Question duplicated successfully", {
      variant: "success",
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  };

  const handleSave = async () => {
    const selectedBotId = localStorage.getItem("selectedBotId");
    const userID = localStorage.getItem("userId") || JSON.parse(localStorage.getItem("user"))?._id;
    if (!userID) {
      throw new Error("Missing userId");
    }
    const cleanItems = droppedItems.map(({ icon, label, defaultLabel, ...rest }) => rest);
    const payload = { flowSetupSetting: { question: { list: cleanItems } } };
    try {
      if (!selectedBotId) {
        toast.error('No bot selected. Select a bot from sidebar first.');
        return;
      }
      const response = await updateBot(selectedBotId, payload);
      if (response) toast.success("Saved successfully");
      else toast.error("Error saving the questions");
    } catch (error) {
      console.error("Error saving the questions:", error);
    }
  };

  const handleConditionalFlow = (id) => {
    const item = droppedItems.find((q) => q.id === id);
    if (!item) return;
    setInitialTab(3);
    setEditingItem(item);
    setOpenEdit(true);
  };

  const openTemplateDialog = async () => {
    const selectedBotId = localStorage.getItem("selectedBotId");
    if (!selectedBotId) {
      toast.error("No bot selected. Select a bot first.");
      return;
    }
    setTemplateOpen(true);
    if (templates.length === 0) {
      setTemplatesLoading(true);
      try {
        const res = await listTemplates();
        setTemplates(res.templates || []);
      } catch (err) {
        toast.error("Failed to load templates");
      } finally {
        setTemplatesLoading(false);
      }
    }
  };

  const handleApplyTemplate = async (tpl) => {
    const selectedBotId = localStorage.getItem("selectedBotId");
    if (!selectedBotId || !tpl) return;
    setApplyingTemplate(true);
    try {
      const res = await applyTemplate(selectedBotId, tpl.key);
      toast.success(res.message || "Template applied");
      const bot = await getBotById(selectedBotId);
      const list = bot?.flowSetupSetting?.question?.list || [];
      const cleaned = list.map((q) => ({
        ...q,
        id: q.id || generateUniqueId(),
        text: q.text || "",
        flexDirection: q.flexDirection || "row",
        style: q.style || "button",
        skipOption: !!q.skipOption,
        required: !!q.required,
        errorMessage: q.errorMessage || "",
        options: q.options || [],
        validations: q.validations || {},
        settings: q.settings || {},
        conditions: q.conditions || [],
        media: q.media || "",
        otherOption: !!q.otherOption,
        shuffleOptions: !!q.shuffleOptions,
        language: q.language || "en",
        defaultSelected: q.defaultSelected ?? null,
        imageChoices: q.imageChoices || [],
      }));
      setDroppedItems(cleaned);
      enqueueSnackbar(`${cleaned.length} questions loaded`, {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      setTemplateOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to apply template");
    } finally {
      setApplyingTemplate(false);
    }
  };

  // Build a full canvas-ready question item from a minimal AI-generated question.
  const buildAiItem = (q) => {
    const meta = AI_TYPE_META[q.type] || AI_TYPE_META.question;
    return {
      id: generateUniqueId(),
      type: q.type,
      text: q.text || "",
      icon: meta.icon,
      label: meta.label,
      color: meta.color,
      flexDirection: "row",
      style: "button",
      skipOption: false,
      required: !!q.required,
      errorMessage: "",
      options: Array.isArray(q.options) ? q.options : [],
      validations: {},
      settings: {},
      conditions: [],
      media: "",
      otherOption: false,
      shuffleOptions: false,
      language: "en",
      defaultSelected: null,
      imageChoices: [],
    };
  };

  const handleGenerateAI = async () => {
    const selectedBotId = localStorage.getItem("selectedBotId");
    if (!selectedBotId) {
      toast.error("No bot selected. Select a bot first.");
      return;
    }
    setAiLoading(true);
    try {
      const res = await generateQuestions(selectedBotId, {
        prompt: aiPrompt.trim(),
        count: aiCount,
      });
      const generated = (res?.questions || []).map(buildAiItem);
      if (generated.length === 0) {
        toast.error("AI did not return any questions. Try a different prompt.");
        return;
      }
      setDroppedItems((prev) => [...prev, ...generated]);
      enqueueSnackbar(`${generated.length} question${generated.length > 1 ? "s" : ""} added by AI`, {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      setAiOpen(false);
      setAiPrompt("");
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to generate questions");
    } finally {
      setAiLoading(false);
    }
  };

  const filteredItems = droppedItems.filter((item) =>
    item.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const getQuestions = async () => {
      try {
        const selectedBotId = localStorage.getItem("selectedBotId");
        if (!selectedBotId) return;
        const botData = await getBotById(selectedBotId);
        setDroppedItems(botData?.flowSetupSetting?.question?.list || []);
        if (botData?.botSettings) setBotSettings(botData.botSettings);
      } catch (error) {
        console.error("Error fetching questions:", error);
        enqueueSnackbar("Error fetching questions", { variant: "error" });
      }
    };
    getQuestions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const themeColors = botSettings.themeColors || {};
  const themeAccent = themeColors.header || themeColors.option || "#6366f1";

  return (
    <Box
      ref={dropRef}
      sx={{
        flex: 1,
        minHeight: "60vh",
        p: { xs: 1.5, md: 2.5 },
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(12px)",
        transition: "background 0.3s",
        position: "relative",
      }}
    >
      {/* Top Control Bar */}
      <Box sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        gap: 1.5,
        alignItems: { xs: "stretch", sm: "center" },
        mb: 0.5,
      }}>
        <TextField
          placeholder="Search questions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: { xs: "100%", sm: 280 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px", bgcolor: "#fff", fontSize: 13,
              "& fieldset": { borderColor: "#e5e7eb" },
              "&:hover fieldset": { borderColor: "#c7d2fe" },
              "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: 1.5 },
            },
          }}
        />

        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}>
          <Select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            size="small"
            sx={{
              minWidth: 100,
              borderRadius: "10px",
              bgcolor: "#fff",
              fontSize: 13,
              "& fieldset": { borderColor: "#e5e7eb" },
            }}
          >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="hi">Hindi</MenuItem>
            <MenuItem value="es">Spanish</MenuItem>
            <MenuItem value="fr">French</MenuItem>
          </Select>

          <Button
            onClick={() => setOpenPreview(true)}
            startIcon={<PreviewIcon />}
            variant="outlined"
            sx={{
              borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 13,
              borderColor: "#e5e7eb", color: "#374151", px: 2,
              "&:hover": { borderColor: "#6366f1", bgcolor: "#6366f106" },
            }}
          >
            Preview
          </Button>

          <Button
            onClick={() => setAiOpen(true)}
            startIcon={<AutoAwesomeIcon />}
            variant="outlined"
            sx={{
              borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 13,
              borderColor: "#8b5cf6", color: "#8b5cf6", px: 2,
              background: "linear-gradient(135deg, #f5f3ff 0%, #fff 100%)",
              "&:hover": { borderColor: "#8b5cf6", bgcolor: "#f5f3ff" },
            }}
          >
            AI Generate
          </Button>

          <Button
            onClick={openTemplateDialog}
            startIcon={<AutoFixHighIcon />}
            variant="outlined"
            sx={{
              borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 13,
              borderColor: "#ec4899", color: "#ec4899", px: 2,
              background: "linear-gradient(135deg, #fdf2f8 0%, #fff 100%)",
              "&:hover": { borderColor: "#ec4899", bgcolor: "#fdf2f8" },
            }}
          >
            Use Template
          </Button>

          <Button
            onClick={handleSave}
            startIcon={<SaveIcon />}
            variant="contained"
            sx={{
              borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 13,
              bgcolor: themeAccent, px: 2.5,
              "&:hover": { bgcolor: themeAccent, filter: "brightness(0.9)" },
            }}
          >
            Save
          </Button>
        </Box>
      </Box>

      {/* Drop Area */}
      <Box sx={{
        flex: 1,
        borderRadius: 3,
        border: isOver ? "2px dashed #6366f1" : "2px dashed transparent",
        bgcolor: isOver ? "#6366f106" : "transparent",
        transition: "all 0.3s",
        p: { xs: 1, md: 2 },
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}>
        {filteredItems.length === 0 ? (
          <Box sx={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 1.5, py: 6,
            color: "#d1d5db",
          }}>
            <WidgetsIcon sx={{ fontSize: 48, opacity: 0.4 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#9ca3af" }}>
              {droppedItems.length === 0
                ? "Drag components here to build your flow"
                : "No matching questions found"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#d1d5db" }}>
              {droppedItems.length === 0
                ? "Pick items from the sidebar and drop them here"
                : "Try a different search term"}
            </Typography>
          </Box>
        ) : (
          filteredItems.map((item) => {
            const originalIndex = droppedItems.findIndex((di) => di.id === item.id);
            return (
              <QuestionDraggableItem
                key={item.id}
                index={originalIndex}
                item={item}
                moveItem={(from, to) => {
                  const updated = [...droppedItems];
                  const [moved] = updated.splice(from, 1);
                  updated.splice(to, 0, moved);
                  setDroppedItems(updated);
                }}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                onConditional={handleConditionalFlow}
                accentColor={themeAccent}
              />
            );
          })
        )}
      </Box>

      {/* Edit Popup */}
      {openEdit && editingItem && (
        <EditQuestionPopup
          openEdit={openEdit}
          handleCloseEdit={handleCloseEdit}
          editingItem={editingItem}
          onUpdate={handleUpdate}
          allQuestions={droppedItems}
          initialTabIndex={initialTab}
        />
      )}

      {/* Bot Preview */}
      <BotPreviewDialogPopup
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        droppedItems={droppedItems}
      />

      {/* AI Generate Questions */}
      <Dialog
        open={aiOpen}
        onClose={() => !aiLoading && setAiOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)", px: 3, py: 2.5, color: "#fff" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 22 }} />
            <Typography sx={{ fontWeight: 800, fontSize: 17 }}>Generate Questions with AI</Typography>
          </Box>
          <Typography sx={{ fontSize: 12, opacity: 0.9, mt: 0.5 }}>
            Describe what you want to ask visitors. The generated questions are added to your current flow.
          </Typography>
        </Box>
        <DialogContent sx={{ p: 2.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", mb: 0.8 }}>
            What should the bot ask about?
          </Typography>
          <TextField
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder="e.g. Qualify leads for a real estate agency — budget, location, property type, contact details"
            multiline
            minRows={3}
            fullWidth
            disabled={aiLoading}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
          />
          <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 0.8 }}>
            Leave empty for a general set of lead-qualifying questions.
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Number of questions
            </Typography>
            <Select
              value={aiCount}
              onChange={(e) => setAiCount(e.target.value)}
              size="small"
              disabled={aiLoading}
              sx={{ minWidth: 80, borderRadius: "10px", bgcolor: "#fff", fontSize: 13 }}
            >
              {[3, 5, 8, 10, 12].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, borderTop: "1px solid #f3f4f6", bgcolor: "#fafafa" }}>
          <Button onClick={() => setAiOpen(false)} disabled={aiLoading} sx={{ textTransform: "none", fontWeight: 600, color: "#6b7280" }}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerateAI}
            disabled={aiLoading}
            variant="contained"
            startIcon={aiLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <AutoAwesomeIcon />}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2.5,
              background: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
              "&:hover": { background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)" },
              "&.Mui-disabled": { background: "#e5e7eb", color: "#9ca3af" },
            }}
          >
            {aiLoading ? "Generating…" : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={templateOpen}
        onClose={() => !applyingTemplate && setTemplateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)", px: 3, py: 2.5, color: "#fff" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoFixHighIcon sx={{ fontSize: 22 }} />
            <Typography sx={{ fontWeight: 800, fontSize: 17 }}>Industry Templates</Typography>
          </Box>
          <Typography sx={{ fontSize: 12, opacity: 0.9, mt: 0.5 }}>
            Load a pre-built flow that demonstrates every question type. Your current flow will be replaced.
          </Typography>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {templatesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={28} sx={{ color: "#ec4899" }} />
            </Box>
          ) : (
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {templates.map((tpl) => (
                <Box
                  key={tpl.key}
                  onClick={() => !applyingTemplate && setSelectedTemplate(tpl.key)}
                  sx={{
                    p: 2, borderRadius: 3, cursor: "pointer", transition: "all 0.2s",
                    border: "2px solid",
                    borderColor: selectedTemplate === tpl.key ? "#ec4899" : "#e5e7eb",
                    bgcolor: selectedTemplate === tpl.key ? "#fdf2f8" : "#fff",
                    "&:hover": { borderColor: "#ec4899" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                    {selectedTemplate === tpl.key ? (
                      <CheckCircleIcon sx={{ fontSize: 18, color: "#ec4899" }} />
                    ) : (
                      <Box sx={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid #d1d5db" }} />
                    )}
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{tpl.name}</Typography>
                    <Chip label={tpl.industry} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: "#fdf2f8", color: "#be185d", ml: "auto" }} />
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: "#6b7280", mb: 1 }}>{tpl.description}</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                    <Chip label={`${tpl.questionCount} questions`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: "#f0fdfa", color: "#0f766e" }} />
                    <Chip label={`${tpl.typeCoverage.length} types`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: "#eef2ff", color: "#4338ca" }} />
                    {tpl.typeCoverage.slice(0, 6).map((t) => (
                      <Chip key={t} label={t} size="small" sx={{ height: 18, fontSize: 9, bgcolor: "#f9fafb", color: "#374151" }} />
                    ))}
                    {tpl.typeCoverage.length > 6 && (
                      <Chip label={`+${tpl.typeCoverage.length - 6} more`} size="small" sx={{ height: 18, fontSize: 9, bgcolor: "#f9fafb", color: "#6b7280" }} />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, borderTop: "1px solid #f3f4f6", bgcolor: "#fafafa" }}>
          <Button onClick={() => setTemplateOpen(false)} disabled={applyingTemplate} sx={{ textTransform: "none", fontWeight: 600, color: "#6b7280" }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleApplyTemplate(templates.find((t) => t.key === selectedTemplate))}
            disabled={!selectedTemplate || applyingTemplate}
            variant="contained"
            startIcon={applyingTemplate ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <AutoFixHighIcon />}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2.5,
              background: "linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)",
              "&:hover": { background: "linear-gradient(135deg, #db2777 0%, #e11d48 100%)" },
              "&.Mui-disabled": { background: "#e5e7eb", color: "#9ca3af" },
            }}
          >
            {applyingTemplate ? "Applying…" : "Apply Template"}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </Box>
  );
};

export default FlowCanvasComponent;
